import React, { useState } from 'react';

// Living documentation. Every module of the Free Builder kit, mapped to the
// file paths that implement it. Sourced from the HR without Borders reference
// codebase; mirrors what's in the token-gated AI kit and the PDF course.
const MODULES = [
  // ---------- 01 USER MANAGEMENT ----------
  {
    n: 1,
    slug: 'user-management',
    title: 'User Management',
    status: 'complete',
    chapter: 'Chapter 06 — User Management',
    description: 'Auth, accounts, invites, roles, capabilities, suspension, API keys, password reset. Full parity with BidLight + Prigmar.',
    backend: {
      models: ['User', 'Role', 'Invite', 'AccessToken'],
      controllers: ['user.ctrl.js', 'invite.ctrl.js', 'role.ctrl.js', 'accessToken.ctrl.js'],
      routes: ['/api/auth', '/api/users', '/api/invites', '/api/roles', '/api/access-tokens'],
      middlewares: ['requireAuth', 'requireCapability'],
      utils: ['authentication.js (JWT)', 'capabilities.js (catalog)', 'email.js (stub)'],
    },
    frontend: {
      pages: [
        'Login', 'Signup', 'ForgotPassword', 'ResetPassword/:token', 'VerifyEmail/:token',
        'AcceptInvite/:token', 'Profile', 'admin/Users', 'admin/Invites', 'admin/Roles',
        'account/ApiKeys',
      ],
      reducers: ['authReducer', 'usersAdminReducer', 'invitesReducer', 'rolesReducer', 'apiKeysReducer'],
      actions: ['authActions', 'userAdminActions', 'inviteActions', 'roleActions', 'apiKeyActions'],
      constants: ['AUTH', 'USERS_ADMIN', 'INVITES', 'ROLES', 'APIKEYS'],
    },
    endpoints: [
      'POST /api/auth/signup',
      'POST /api/auth/login',
      'POST /api/auth/logout',
      'GET  /api/auth/me',
      'PUT  /api/auth/me',
      'POST /api/auth/me/change-password',
      'POST /api/auth/me/resend-verification',
      'DELETE /api/auth/me',
      'POST /api/auth/request-password-reset',
      'POST /api/auth/reset-password',
      'GET  /api/auth/verify-email/:token',
      'POST /api/auth/verify-username',
      'GET  /api/users (admin list)',
      'PUT  /api/users/:id/role',
      'PUT  /api/users/:id/suspend',
      'PUT  /api/users/:id/capabilities',
      'DELETE /api/users/:id',
      'POST /api/invites (create)',
      'POST /api/invites/bulk',
      'GET  /api/invites',
      'GET  /api/invites/by-token/:token',
      'POST /api/invites/accept',
      'POST /api/invites/decline/:token',
      'POST /api/invites/:id/resend',
      'DELETE /api/invites/:id',
      'GET  /api/roles',
      'POST /api/roles',
      'PUT  /api/roles/:id',
      'DELETE /api/roles/:id',
      'POST /api/roles/ensure-system',
      'GET  /api/roles/capabilities',
      'GET  /api/access-tokens',
      'POST /api/access-tokens',
      'POST /api/access-tokens/:id/rotate',
      'DELETE /api/access-tokens/:id',
    ],
  },

  // ---------- 02 PERMISSION MANAGEMENT ----------
  {
    n: 2,
    slug: 'permission-management',
    title: 'Permission Management',
    status: 'complete',
    chapter: 'Chapter 07 — Permission Management',
    description: 'Role-based access via a flat capability catalog. Same mechanism gates roles and plan tiers. Custom roles override defaults; per-user overrides override roles.',
    backend: {
      models: ['Role (with capabilities array)'],
      controllers: ['role.ctrl.js (manages role bundles + per-user capability overrides)'],
      routes: ['/api/roles/*', '/api/users/:id/capabilities'],
      middlewares: ['requireAuth', 'requirePermission(roles[])', 'requireCapability(key)'],
      utils: ['capabilities.js — catalog (CATALOG[]) and role defaults (DEFAULTS{})'],
    },
    frontend: {
      pages: ['admin/Roles (matrix UI)', 'admin/Users → capability override panel'],
      reducers: ['rolesReducer'],
      actions: ['roleActions (loadRoles, createRole, updateRole, deleteRole, ensureSystemRoles)'],
      constants: ['ROLES'],
    },
    endpoints: [
      'GET  /api/roles                    (list custom + built-in roles)',
      'POST /api/roles                    (create custom role)',
      'PUT  /api/roles/:id                (update capabilities)',
      'DELETE /api/roles/:id              (cannot delete built-ins)',
      'POST /api/roles/ensure-system      (idempotent seed: customer/sales/manager/admin)',
      'GET  /api/roles/capabilities       (the public capability catalog)',
      'PUT  /api/users/:id/role           (admin sets role)',
      'PUT  /api/users/:id/capabilities   (per-user grant/revoke overrides)',
    ],
  },

  // ---------- 03 INVENTORY / PROJECT MANAGEMENT ----------
  {
    n: 3,
    slug: 'inventory',
    title: 'Inventory & Project Management',
    status: 'complete',
    chapter: 'Chapter 08 — Project Management',
    description: 'The core domain CRUD: memberships list, search, filter, view detail, admin create/edit/delete. Status enum for lifecycle (draft → available → reserved → sold → archived). View counter, photos, features.',
    backend: {
      models: ['Membership', 'Showroom (tenant)'],
      controllers: ['membership.ctrl.js (list/get/create/update/remove + status transitions)'],
      routes: ['/api/memberships'],
      middlewares: ['requireAuth', 'requirePermission(["sales","manager","admin"])'],
      utils: ['activityLogs.js (every mutation logged)'],
    },
    frontend: {
      pages: ['Home (featured memberships)', 'Inventory (search/filter/list)', 'MembershipDetail (public)', 'admin/Memberships (CRUD)', 'Dashboard (staff)'],
      reducers: ['membershipReducer (list, detail, filters, pagination)'],
      actions: ['membershipActions (loadMemberships, loadMembership, createMembership, updateMembership, removeMembership, setFilter)'],
      constants: ['MEMBERSHIPS'],
    },
    endpoints: [
      'GET    /api/memberships              (public — list, search, filter)',
      'GET    /api/memberships/:id          (public — detail, increments view count)',
      'POST   /api/memberships              (sales+ — create new listing)',
      'PUT    /api/memberships/:id          (sales+ — update fields or status)',
      'DELETE /api/memberships/:id          (manager+ — soft-delete via status=archived)',
    ],
  },

  // ---------- 04 ADMIN OPERATIONS ----------
  {
    n: 4,
    slug: 'admin-ops',
    title: 'Admin Operations',
    status: 'complete',
    chapter: 'Chapter 08 — Project Management & Admin Tools',
    description: 'The operator cockpit. System probe, idempotent demo seeding, recent activity feed, impersonation hooks. Every action audit-logged.',
    backend: {
      models: ['ActivityLog'],
      controllers: ['(admin actions inline in routes/adminPanel.js)'],
      routes: ['/api/admin/system', '/api/admin/seed-demo'],
      middlewares: ['requireAuth', 'requirePermission(["admin"])'],
      utils: ['activityLogs.js'],
    },
    frontend: {
      pages: ['Admin (cockpit: stats + activity feed + seed button)'],
      reducers: ['(consumes analyticsSummary state from membershipReducer)'],
      actions: ['adminActions (seedDemo, fetchSystemInfo)'],
      constants: ['ADMIN'],
    },
    endpoints: [
      'GET  /api/admin/system        (env probe — db, node version, uptime)',
      'POST /api/admin/seed-demo     (idempotent — creates 20 demo memberships if inventory empty)',
    ],
  },

  // ---------- 05 ANALYTICS ----------
  {
    n: 5,
    slug: 'analytics',
    title: 'Analytics & Measurement',
    status: 'complete',
    chapter: 'Chapter 09 — Analytics & Measurement',
    description: 'Three-layer measurement: traffic (sessions in), revenue (Stripe), events (activity log). Dashboard summary runs five count queries in parallel. Activity log doubles as audit trail.',
    backend: {
      models: ['ActivityLog (indexed on actor, action, createdAt)'],
      controllers: ['(inline in routes/analytics.js)'],
      routes: ['/api/analytics/summary', '/api/analytics/activity'],
      middlewares: ['requireAuth', 'requirePermission(["manager","admin"])'],
      utils: ['activityLogs.record() — fire-and-forget logger'],
    },
    frontend: {
      pages: ['Admin (embeds summary + activity table)', 'Dashboard (per-user stats)'],
      reducers: ['analyticsReducer (summary, activity)'],
      actions: ['analyticsActions (loadSummary, loadActivity)'],
      constants: ['ANALYTICS'],
    },
    endpoints: [
      'GET /api/analytics/summary       (30-day rollup: signups, leads, memberships, MRR)',
      'GET /api/analytics/activity      (paginated audit trail)',
    ],
  },

  // ---------- 06 LEADS / GROWTH ----------
  {
    n: 6,
    slug: 'leads',
    title: 'Leads & Sales Funnel',
    status: 'complete',
    chapter: 'Chapter 12 — Growth Engine',
    description: 'Public lead capture (no auth required) + staff list/update with status pipeline (new → contacted → qualified → converted/lost). Notes thread, assignment, source attribution.',
    backend: {
      models: ['Lead (with notes[], status enum, source, intent)'],
      controllers: ['lead.ctrl.js (create, list, updateStatus, addNote, assign)'],
      routes: ['/api/leads'],
      middlewares: ['requireAuth (for staff routes — public POST is open)', 'requirePermission(["sales","manager","admin"])'],
      utils: ['activityLogs.js'],
    },
    frontend: {
      pages: ['MembershipDetail (LeadForm embedded)', 'admin/Leads (pipeline)'],
      reducers: ['leadReducer (list, filters, currentLead)'],
      actions: ['leadActions (submitLead, loadLeads, updateLeadStatus, addNote, assignLead)'],
      constants: ['LEADS'],
    },
    endpoints: [
      'POST /api/leads             (public — capture from contact form)',
      'GET  /api/leads             (staff — list, filter by status/source/assignedTo)',
      'PUT  /api/leads/:id         (staff — change status / assign)',
      'POST /api/leads/:id/notes   (staff — append note)',
    ],
  },

  // ---------- 07 BILLING ----------
  {
    n: 7,
    slug: 'billing',
    title: 'Billing & Plans',
    status: 'complete',
    chapter: 'Chapter 11 — Billing — Stripe Integration',
    description: 'Stripe Checkout integration with stub mode for demos. Three endpoints — plan list, checkout session, webhook. Plan tier becomes a capability bundle reusing the permission middleware.',
    backend: {
      models: ['(plan/subscription state on Showroom or User doc)'],
      controllers: ['(inline in routes/billing.js)'],
      routes: ['/api/billing/plans', '/api/billing/checkout', '/api/billing/webhook'],
      middlewares: ['requireAuth (checkout only)'],
      utils: ['bin/config.js (stripeSecret, stripeWebhookSecret)'],
    },
    frontend: {
      pages: ['Pricing / Plans', 'account/Billing (current plan, change plan, invoices)', 'billing/Success', 'billing/Cancel'],
      reducers: ['billingReducer (plans, subscription, invoices)'],
      actions: ['billingActions (loadPlans, startCheckout, loadSubscription)'],
      constants: ['BILLING'],
    },
    endpoints: [
      'GET  /api/billing/plans         (public — catalog: starter/pro/enterprise)',
      'POST /api/billing/checkout      (auth — returns Stripe Checkout URL or stub)',
      'POST /api/billing/webhook       (raw body — Stripe event handler)',
    ],
  },

  // ---------- 08 ACCESS TOKENS / API KEYS ----------
  {
    n: 8,
    slug: 'api-keys',
    title: 'Programmatic Access — API Keys',
    status: 'complete',
    chapter: 'Chapter 06 — User Management (PATs subsystem)',
    description: 'Personal access tokens à la GitHub. Hashed on create, plaintext returned exactly once, prefix-only in UI, last-used tracking, rotate without revoke, scopes (read/write/admin).',
    backend: {
      models: ['AccessToken (hashed, prefix, scopes[], lastUsedAt)'],
      controllers: ['accessToken.ctrl.js (list, create, rotate, revoke)'],
      routes: ['/api/access-tokens'],
      middlewares: ['requireAuth (browser session)', 'API key auth (header → AccessToken lookup)'],
      utils: ['authentication.js (Bearer token verify branch)'],
    },
    frontend: {
      pages: ['account/ApiKeys (list, create with one-time reveal, rotate, revoke)'],
      reducers: ['apiKeysReducer'],
      actions: ['apiKeyActions (loadKeys, createKey, rotateKey, revokeKey)'],
      constants: ['APIKEYS'],
    },
    endpoints: [
      'GET    /api/access-tokens          (list your PATs — prefix only)',
      'POST   /api/access-tokens          (create — returns plaintext once)',
      'POST   /api/access-tokens/:id/rotate',
      'DELETE /api/access-tokens/:id      (revoke)',
    ],
  },

  // ---------- 09 NOTIFICATIONS ----------
  {
    n: 9,
    slug: 'notifications',
    title: 'Notifications',
    status: 'stub',
    chapter: 'Chapter 12 — Growth Engine (support layer)',
    description: 'In-app notifications + email digest scaffolding. Stubs for the broadcaster, per-user preferences, and the email-template engine.',
    backend: {
      models: ['Notification (recipient, type, body, readAt)', 'NotificationPreference'],
      controllers: ['notification.ctrl.js (list, markRead, updatePrefs)'],
      routes: ['/api/notifications', '/api/notifications/prefs'],
      middlewares: ['requireAuth'],
      utils: ['email.js (provider stub — Resend / Postmark adapter)'],
    },
    frontend: {
      pages: ['Header bell dropdown', 'account/Notifications (preferences + history)'],
      reducers: ['notificationsReducer (unreadCount, list)'],
      actions: ['notificationActions (loadNotifications, markRead, updatePrefs)'],
      constants: ['NOTIFICATIONS'],
    },
    endpoints: [
      'GET  /api/notifications              (paginated)',
      'PUT  /api/notifications/:id/read',
      'PUT  /api/notifications/read-all',
      'GET  /api/notifications/prefs',
      'PUT  /api/notifications/prefs',
    ],
  },

  // ---------- 10 SUPPORT / CHAT ----------
  {
    n: 10,
    slug: 'support',
    title: 'Support & Knowledge Base',
    status: 'stub',
    chapter: 'Chapter 12 — Growth Engine (support pyramid)',
    description: 'The support pyramid in the framework — docs > AI chat > human escalation. Stubs for conversation threads, knowledge articles, AI deflection, and operator escalation queue.',
    backend: {
      models: ['Conversation', 'Message', 'KnowledgeArticle'],
      controllers: ['conversation.ctrl.js', 'article.ctrl.js'],
      routes: ['/api/conversations', '/api/knowledge'],
      middlewares: ['requireAuth (customer)', 'requirePermission (operator side)'],
      utils: ['email.js'],
    },
    frontend: {
      pages: ['support/Inbox (customer)', 'admin/Support (operator queue)', 'KnowledgeBase (public)'],
      reducers: ['supportReducer'],
      actions: ['supportActions (loadConversations, postMessage, escalate)'],
      constants: ['SUPPORT'],
    },
    endpoints: [
      'GET  /api/conversations            (customer — own threads)',
      'POST /api/conversations            (start new)',
      'POST /api/conversations/:id/messages',
      'GET  /api/admin/conversations      (operator queue)',
      'GET  /api/knowledge                (public articles)',
    ],
  },

  // ---------- 11 CONTENT / SEO ----------
  {
    n: 11,
    slug: 'content',
    title: 'Content & SEO',
    status: 'stub',
    chapter: 'Chapter 04 — SEO from Day One',
    description: 'Public blog/content surface: posts, sitemaps, schema.org markup, redirects. Stubs for an editor, draft/publish flow, and SEO audit report.',
    backend: {
      models: ['Article', 'Redirect'],
      controllers: ['article.ctrl.js'],
      routes: ['/api/articles', '/sitemap.xml', '/robots.txt'],
      middlewares: ['requirePermission(["marketing","admin"])'],
      utils: ['(rendering helpers)'],
    },
    frontend: {
      pages: ['Blog (list)', 'Article (detail)', 'admin/Articles (editor)'],
      reducers: ['articlesReducer'],
      actions: ['articleActions'],
      constants: ['ARTICLES'],
    },
    endpoints: [
      'GET  /api/articles                  (public — published)',
      'GET  /api/articles/:slug            (public — detail)',
      'POST /api/articles                  (admin — create draft)',
      'PUT  /api/articles/:id              (admin — update / publish)',
      'GET  /sitemap.xml',
    ],
  },

  // ---------- 12 INTEGRATIONS / WEBHOOKS ----------
  {
    n: 12,
    slug: 'integrations',
    title: 'Integrations & Webhooks',
    status: 'stub',
    chapter: 'Chapter 11 — Billing + extensions',
    description: 'Outbound webhooks to customer endpoints. Stubs for webhook destinations, retry queue, signed delivery, replay UI. API keys (built) cover inbound.',
    backend: {
      models: ['WebhookEndpoint', 'WebhookDelivery'],
      controllers: ['webhook.ctrl.js'],
      routes: ['/api/webhooks/endpoints', '/api/webhooks/deliveries'],
      middlewares: ['requireAuth', 'requireCapability("webhooks.manage")'],
      utils: ['webhookSigner.js (HMAC-SHA256)'],
    },
    frontend: {
      pages: ['account/Webhooks (CRUD + delivery log + replay)'],
      reducers: ['webhooksReducer'],
      actions: ['webhookActions'],
      constants: ['WEBHOOKS'],
    },
    endpoints: [
      'GET  /api/webhooks/endpoints',
      'POST /api/webhooks/endpoints',
      'PUT  /api/webhooks/endpoints/:id',
      'DELETE /api/webhooks/endpoints/:id',
      'GET  /api/webhooks/deliveries        (paginated, filter by status/endpoint)',
      'POST /api/webhooks/deliveries/:id/replay',
    ],
  },
];

const STATUS = {
  complete: { label: 'Complete',  color: '#1a7a32', bg: '#e8f5ec' },
  partial:  { label: 'Partial',   color: '#3282b8', bg: '#e8f1f9' },
  stub:     { label: 'Stub / Roadmap', color: '#aa6500', bg: '#fff4dd' },
  pending:  { label: 'Pending',   color: '#6c7d92', bg: '#eef1f4' },
};

export default function Docs() {
  const [open, setOpen] = useState(MODULES[0].slug);

  return (
    <section className="section">
      <header style={{textAlign:'center',marginBottom:'2.4rem',maxWidth:'780px',marginLeft:'auto',marginRight:'auto'}}>
        <p style={{fontSize:'.82rem',letterSpacing:'.14em',textTransform:'uppercase',fontWeight:700,color:'#b53b3b',margin:'0 0 .8rem'}}>HR without Borders · Free Builder reference</p>
        <h1>Module documentation</h1>
        <p className="muted">Every module of the Free Builder framework, mapped to the file paths that implement it. This is the canonical source of truth — the AI kit endpoints and the PDF course both regenerate from this list.</p>
      </header>

      <div className="card">
        <h2>The 12 modules</h2>
        <ul style={{listStyle:'none',padding:0,margin:0}}>
          {MODULES.map(m => {
            const s = STATUS[m.status];
            return (
              <li key={m.slug} style={{display:'grid',gridTemplateColumns:'40px 1fr auto',gap:'1rem',padding:'.7rem 0',borderBottom:'1px solid #e6ebf0',alignItems:'baseline'}}>
                <code style={{color:'#3282b8',fontWeight:700}}>{String(m.n).padStart(2, '0')}</code>
                <div>
                  <a href={`#${m.slug}`} onClick={(e) => { e.preventDefault(); setOpen(m.slug); }} style={{fontWeight:700,color:'#0a1a2e'}}>{m.title}</a>
                  <div style={{fontSize:'.78rem',color:'#3282b8',marginTop:'.1rem',fontWeight:600}}>{m.chapter}</div>
                  <div className="muted" style={{fontSize:'.85rem',marginTop:'.15rem'}}>{m.description}</div>
                </div>
                <span style={{background:s.bg,color:s.color,padding:'.2rem .6rem',borderRadius:'4px',fontSize:'.72rem',fontWeight:800,letterSpacing:'.08em',textTransform:'uppercase',whiteSpace:'nowrap'}}>{s.label}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {MODULES.map(m => m.slug === open ? (
        <article key={m.slug} id={m.slug} className="card">
          <div className="section-head">
            <h2>{m.title}</h2>
            <span style={{background:STATUS[m.status].bg,color:STATUS[m.status].color,padding:'.2rem .6rem',borderRadius:'4px',fontSize:'.72rem',fontWeight:800,letterSpacing:'.08em',textTransform:'uppercase'}}>{STATUS[m.status].label}</span>
          </div>
          <p style={{color:'#3282b8',fontWeight:600,marginTop:'-.3rem'}}>{m.chapter}</p>
          <p>{m.description}</p>

          {m.backend && (
            <>
              <h3>Backend structure</h3>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:'1rem'}}>
                {['models','controllers','routes','middlewares','utils'].map(k => m.backend[k] && (
                  <div key={k}>
                    <h4 style={{fontSize:'.78rem',letterSpacing:'.12em',textTransform:'uppercase',color:'#6c7d92',margin:'0 0 .4rem'}}>{k}</h4>
                    <ul style={{margin:0,paddingLeft:'1.2rem',lineHeight:'1.7'}}>
                      {m.backend[k].map(item => <li key={item}><code style={{fontSize:'.85rem'}}>{item}</code></li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}

          {m.frontend && (
            <>
              <h3 style={{marginTop:'1.4rem'}}>Frontend structure</h3>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:'1rem'}}>
                {['pages','reducers','actions','constants'].map(k => m.frontend[k] && (
                  <div key={k}>
                    <h4 style={{fontSize:'.78rem',letterSpacing:'.12em',textTransform:'uppercase',color:'#6c7d92',margin:'0 0 .4rem'}}>{k}</h4>
                    <ul style={{margin:0,paddingLeft:'1.2rem',lineHeight:'1.7'}}>
                      {m.frontend[k].map(item => <li key={item}><code style={{fontSize:'.85rem'}}>{item}</code></li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}

          {m.endpoints && (
            <>
              <h3 style={{marginTop:'1.4rem'}}>API endpoints ({m.endpoints.length})</h3>
              <pre style={{background:'#0a1a2e',color:'#d6e0e9',padding:'1rem',borderRadius:'8px',fontSize:'.82rem',lineHeight:'1.5',overflow:'auto'}}>
{m.endpoints.join('\n')}
              </pre>
            </>
          )}

          {m.status === 'stub' && (
            <p className="muted" style={{marginTop:'1.4rem',padding:'.8rem 1rem',background:'#fff4dd',borderLeft:'3px solid #aa6500',borderRadius:'0 6px 6px 0'}}>
              Roadmap module — the data model and route shape are reserved, the controllers/UI are stubs. Future Free Builder sessions deepen this module to the same depth as the User Management one.
            </p>
          )}
        </article>
      ) : null)}

      <section className="card" style={{marginTop:'2rem'}}>
        <h2>How this maps to the course</h2>
        <p>The free PDF course (<a href="https://free-builder.com/free-pdf-course/">download</a>) walks through each of these modules as one chapter of the 12-chapter framework. The AI-readable kit at <code>/wp-json/free-builder/v1/kit/*</code> serves the same content as JSON + markdown for LLMs and automation. This page, the PDF, and the kit are three views of one source of truth.</p>
      </section>
    </section>
  );
}
